import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Buildings, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useEmpresa } from "@/contexts/EmpresaContext";
import GuidedTour from "@/components/GuidedTour";
import TourHelpButton from "@/components/TourHelpButton";
import { useGuidedTour } from "@/hooks/useGuidedTour";

const TOUR_STORAGE_KEY = "chkr_tour_empresas_seen";

function tourSteps(user) {
  const steps = [
    { target: "[data-tour='empresas-tabla']", title: "Catálogo de empresas", content: "Aquí ves la razón social, RFC, régimen fiscal y datos de contacto de cada empresa registrada en el sistema." },
  ];
  if (user?.role === "super_admin") {
    steps.push({ target: "[data-tour='empresas-nueva']", title: "Registrar una empresa", content: "Solo Super Admin puede dar de alta nuevas empresas: razón social, RFC, domicilio fiscal y representante legal." });
  }
  return steps;
}

export default function Empresas() {
  const { user } = useAuth();
  const { recargarEmpresas } = useEmpresa();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ razon_social: "", rfc: "", regimen_fiscal: "", domicilio_fiscal: "", representante_legal: "", contacto_email: "", contacto_telefono: "" });
  const [busy, setBusy] = useState(false);
  const { active: tourActive, start: startTour, finish: finishTour } = useGuidedTour(TOUR_STORAGE_KEY);

  const load = () => api.get("/empresas").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEditing(null);
    setForm({ razon_social: "", rfc: "", regimen_fiscal: "", domicilio_fiscal: "", representante_legal: "", contacto_email: "", contacto_telefono: "" });
    setOpen(true);
  };
  const startEdit = (e) => {
    setEditing(e);
    setForm(e);
    setOpen(true);
  };

  const submit = async () => {
    setBusy(true);
    try {
      let empresaId = editing?.id;
      if (editing) {
        await api.put(`/empresas/${editing.id}`, form);
      } else {
        empresaId = (await api.post("/empresas", form)).data.id;
      }
      // El tipo va por su propia ruta (solo super_admin); el backend ignora "tipo" en el PUT general.
      const tipoNuevo = form.tipo || "general";
      if (user?.role === "super_admin" && tipoNuevo !== (editing?.tipo || "general")) {
        await api.put(`/empresas/${empresaId}/tipo`, { tipo: tipoNuevo });
        recargarEmpresas();
      }
      toast.success(editing ? "Empresa actualizada" : "Empresa creada");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="empresas-page">
      <PageHeader
        kicker="Catálogos"
        title="Empresas"
        subtitle="Razón social, RFC y configuración fiscal de cada empresa registrada."
        actions={
          <div className="flex gap-2 items-center">
            {user?.role === "super_admin" && (
              <Button onClick={startCreate} className="bg-brand hover:bg-brand-hover" data-testid="empresa-new-btn" data-tour="empresas-nueva">
                <Plus size={16} weight="bold" className="mr-2" /> Nueva empresa
              </Button>
            )}
            <TourHelpButton onClick={startTour} />
          </div>
        }
      />

      <GuidedTour steps={tourSteps(user)} active={tourActive} onFinish={finishTour} />

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-tour="empresas-tabla">
        <div className="overflow-x-auto">
        <table className="data-table" data-testid="empresas-table">
          <thead>
            <tr>
              <th>Razón Social</th>
              <th>RFC</th>
              <th>Régimen Fiscal</th>
              <th>Representante</th>
              <th>Contacto</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="text-center text-slate-500 py-8">Sin empresas</td></tr>
            )}
            {items.map((e) => (
              <tr key={e.id} data-testid={`empresa-row-${e.id}`}>
                <td>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-slate-100 rounded flex items-center justify-center text-slate-700">
                      <Buildings size={18} weight="duotone" />
                    </div>
                    <div>
                      <div className="font-semibold text-ink">
                        {e.razon_social}
                        {e.tipo === "escuela" && (
                          <span className="ml-2 align-middle text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-brand-accent-soft text-brand-accent" data-testid={`empresa-escuela-${e.id}`}>Escuela</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">{e.domicilio_fiscal}</div>
                    </div>
                  </div>
                </td>
                <td className="font-mono text-xs">{e.rfc}</td>
                <td className="text-xs text-slate-600">{e.regimen_fiscal}</td>
                <td>{e.representante_legal}</td>
                <td className="text-xs">{e.contacto_email}<br/>{e.contacto_telefono}</td>
                <td>
                  <Button variant="ghost" size="sm" onClick={() => startEdit(e)} data-testid={`empresa-edit-${e.id}`}>
                    <PencilSimple size={16} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="empresa-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Editar empresa" : "Nueva empresa"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="col-span-1 sm:col-span-2 space-y-1">
              <Label className="label-uppercase">Razón social</Label>
              <Input value={form.razon_social} onChange={(e) => setForm({ ...form, razon_social: e.target.value })} data-testid="empresa-razon-input" />
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">RFC</Label>
              <Input value={form.rfc} onChange={(e) => setForm({ ...form, rfc: e.target.value })} data-testid="empresa-rfc-input" />
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">Régimen fiscal</Label>
              <Input value={form.regimen_fiscal} onChange={(e) => setForm({ ...form, regimen_fiscal: e.target.value })} />
            </div>
            <div className="col-span-1 sm:col-span-2 space-y-1">
              <Label className="label-uppercase">Domicilio fiscal</Label>
              <Input value={form.domicilio_fiscal} onChange={(e) => setForm({ ...form, domicilio_fiscal: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">Representante legal</Label>
              <Input value={form.representante_legal} onChange={(e) => setForm({ ...form, representante_legal: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">Email contacto</Label>
              <Input value={form.contacto_email} onChange={(e) => setForm({ ...form, contacto_email: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">Teléfono</Label>
              <Input value={form.contacto_telefono} onChange={(e) => setForm({ ...form, contacto_telefono: e.target.value })} />
            </div>
            {user?.role === "super_admin" && (
              <div className="space-y-1">
                <Label className="label-uppercase">Tipo de empresa</Label>
                <Select value={form.tipo || "general"} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger data-testid="empresa-tipo-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="escuela">Escuela (docentes por hora-clase)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={busy} className="bg-brand hover:bg-brand-hover" data-testid="empresa-save-btn">
              {busy ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
